'use client';

import React, { useState, useEffect, useMemo } from 'react';
import ApartmentFilters from './ApartmentFilters';
import ApartmentCard from './ApartmentCard';
import ApartmentListingHeader from './ApartmentListingHeader';
import ApartmentEmptyState from './ApartmentEmptyState';
import { getApartments, type Apartment } from '@/lib/supabase/apartments';
import { Loader2, MapPin } from 'lucide-react';

export interface FilterState {
  search: string;
  type: string;
  minPrice: number;
  maxPrice: number;
  guests: number;
  availableOnly: boolean;
  sortBy: string;
}

export default function ApartmentsListingContent() {
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<FilterState>({
    search: '',
    type: 'all',
    minPrice: 0,
    maxPrice: 1000000,
    guests: 0,
    availableOnly: false,
    sortBy: 'featured',
  });
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    getApartments()
      .then((data) => {
        setApartments(data);
        setLoading(false);
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        console.error('[ApartmentsListingContent] Failed to load apartments:', msg);
        setError(`Failed to load apartments: ${msg}`);
        setLoading(false);
      });
  }, []);

  const filtered = useMemo(() => {
    let result = [...apartments];
    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter(
        (a) => a.name.toLowerCase().includes(q) || a.location.toLowerCase().includes(q)
      );
    }
    if (filters.type !== 'all') result = result.filter((a) => a.apartment_type === filters.type);
    if (filters.guests > 0) result = result.filter((a) => a.capacity >= filters.guests);
    if (filters.availableOnly) result = result.filter((a) => a.is_available);
    result = result.filter(
      (a) => Number(a.price_per_night) >= filters.minPrice && Number(a.price_per_night) <= filters.maxPrice
    );
    switch (filters.sortBy) {
      case 'price-asc': result.sort((a, b) => Number(a.price_per_night) - Number(b.price_per_night)); break;
      case 'price-desc': result.sort((a, b) => Number(b.price_per_night) - Number(a.price_per_night)); break;
      case 'featured': result.sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0)); break;
    }
    return result;
  }, [apartments, filters]);

  return (
    <div className="min-h-screen bg-[#F9F7F4]">
      {/* Premium hero header */}
      <div
        className="relative py-16 overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #050D1A 0%, #0B1628 60%, #0F2040 100%)' }}
      >
        {/* Subtle gold radial glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 20% 50%, rgba(201,169,110,0.07) 0%, transparent 60%)' }}
        />
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <MapPin size={12} className="text-[#C9A96E]" />
            <p className="section-label" style={{ color: '#C9A96E' }}>Kigamboni, Dar es Salaam</p>
          </div>
          <div className="w-8 h-px mb-5" style={{ background: '#C9A96E' }} />
          <h1
            className="text-white mb-4"
            style={{
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontSize: 'clamp(1.75rem, 4vw, 3rem)',
              fontWeight: 400,
              letterSpacing: '-0.01em',
              lineHeight: 1.15,
            }}
          >
            All Apartments
          </h1>
          <p className="text-[#8A9BB0] max-w-lg text-sm leading-relaxed">
            Browse our full collection of premium furnished apartments in Kigamboni. Filter by type, price, and availability.
          </p>
        </div>
        {/* Gold bottom line */}
        <div
          className="absolute bottom-0 left-0 right-0 h-px"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(201,169,110,0.4), transparent)' }}
        />
      </div>

      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          <aside className="lg:w-72 shrink-0">
            {/* FIX: prop name is onFilterChange (no 's'), and pass total */}
            <ApartmentFilters
              filters={filters}
              onFilterChange={setFilters}
              total={filtered.length}
            />
          </aside>

          <div className="flex-1 min-w-0">
            <ApartmentListingHeader
              count={filtered.length}
              total={apartments.length}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              sortBy={filters.sortBy}
              onSortChange={(v) => setFilters((f) => ({ ...f, sortBy: v }))}
            />

            {loading ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="animate-spin text-[#C9A96E]" size={36} />
              </div>
            ) : error ? (
              <div className="text-center py-20">
                <p className="text-[#9B2335] font-semibold mb-2">Failed to load apartments</p>
                <p className="text-[#6B6560] text-sm max-w-md mx-auto">{error}</p>
                <button
                  onClick={() => {
                    setError(null);
                    setLoading(true);
                    getApartments()
                      .then((data) => { setApartments(data); setLoading(false); })
                      .catch((err: unknown) => {
                        const msg = err instanceof Error ? err.message : String(err);
                        setError(`Failed to load apartments: ${msg}`);
                        setLoading(false);
                      });
                  }}
                  className="mt-4 px-5 py-2.5 text-xs font-semibold tracking-widest uppercase"
                  style={{ background: '#C9A96E', color: '#050D1A', borderRadius: '2px' }}
                >
                  Retry
                </button>
              </div>
            ) : filtered.length === 0 ? (
              <ApartmentEmptyState
                onReset={() =>
                  setFilters({
                    search: '',
                    type: 'all',
                    minPrice: 0,
                    maxPrice: 1000000,
                    guests: 0,
                    availableOnly: false,
                    sortBy: 'featured',
                  })
                }
              />
            ) : (
              <div
                className={
                  viewMode === 'grid' ?'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6' :'flex flex-col gap-4'
                }
              >
                {filtered.map((apt) => (
                  <ApartmentCard key={apt.id} apartment={apt} viewMode={viewMode} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}